
import React from "react";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell, TableFooter } from "@/components/ui/table";
import { RFPEstimate } from "@/types/project-types";
import { Input } from "@/components/ui/input";

interface EstimationTableProps {
  estimates: RFPEstimate[];
  totals: {
    simple_count: number;
    medium_count: number;
    complex_count: number;
    supercomplex_count: number;
    total_points: number;
    total_dev_days: number;
    total_days: number;
  };
  onUpdateEstimate: (index: number, field: string, value: number) => void;
}

const EstimationTable: React.FC<EstimationTableProps> = ({ 
  estimates, 
  totals,
  onUpdateEstimate
}) => {
  const handleInputChange = (
    index: number, 
    field: string, 
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value) || 0;
    onUpdateEstimate(index, field, value);
  };

  return (
    <div className="mt-6 overflow-x-auto border rounded-lg">
      <Table>
        <TableHeader className="bg-purple-50 dark:bg-gray-800">
          <TableRow>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold">Component Type</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Simple</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Medium</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Complex</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Supercomplex</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Total Components</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Story Points</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Dev Days</TableHead>
            <TableHead className="text-purple-800 dark:text-gray-200 font-semibold text-center">Total Days</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {estimates.map((estimate, index) => (
            <TableRow 
              key={estimate.component_type}
              className={index % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-purple-50/30 dark:bg-gray-800/30"}
            >
              <TableCell className="font-medium">{estimate.component_type}</TableCell>
              <TableCell className="text-center">
                <Input
                  type="number"
                  min="0"
                  className="w-16 h-8 text-center mx-auto"
                  value={estimate.simple_count}
                  onChange={(e) => handleInputChange(index, 'simple_count', e)}
                />
              </TableCell>
              <TableCell className="text-center">
                <Input
                  type="number"
                  min="0"
                  className="w-16 h-8 text-center mx-auto"
                  value={estimate.medium_count}
                  onChange={(e) => handleInputChange(index, 'medium_count', e)}
                />
              </TableCell>
              <TableCell className="text-center">
                <Input
                  type="number"
                  min="0"
                  className="w-16 h-8 text-center mx-auto"
                  value={estimate.complex_count}
                  onChange={(e) => handleInputChange(index, 'complex_count', e)}
                />
              </TableCell>
              <TableCell className="text-center">
                <Input
                  type="number"
                  min="0"
                  className="w-16 h-8 text-center mx-auto"
                  value={estimate.supercomplex_count}
                  onChange={(e) => handleInputChange(index, 'supercomplex_count', e)}
                />
              </TableCell>
              <TableCell className="text-center font-semibold">
                {estimate.simple_count + estimate.medium_count + estimate.complex_count + estimate.supercomplex_count}
              </TableCell>
              <TableCell className="text-center font-semibold">
                {estimate.total_points}
              </TableCell>
              <TableCell className="text-center">
                {estimate.total_dev_days.toFixed(1)}
              </TableCell>
              <TableCell className="text-center">
                {estimate.total_days.toFixed(1)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter className="bg-purple-100 dark:bg-gray-700">
          <TableRow>
            <TableCell className="font-bold">TOTALS</TableCell>
            <TableCell className="text-center font-semibold">{totals.simple_count}</TableCell>
            <TableCell className="text-center font-semibold">{totals.medium_count}</TableCell>
            <TableCell className="text-center font-semibold">{totals.complex_count}</TableCell>
            <TableCell className="text-center font-semibold">{totals.supercomplex_count}</TableCell>
            <TableCell className="text-center font-bold">{totals.simple_count + totals.medium_count + totals.complex_count + totals.supercomplex_count}</TableCell>
            <TableCell className="text-center font-bold">{totals.total_points}</TableCell>
            <TableCell className="text-center font-bold">{totals.total_dev_days.toFixed(1)}</TableCell>
            <TableCell className="text-center font-bold">{totals.total_days.toFixed(1)}</TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};

export default EstimationTable;
